import { useCallback, useEffect, useRef } from 'react';
import { getCurrentUser } from './api';
import { useOneliveSession } from './useOneliveSession';

type G7Api = {
  post: (url: string, data?: unknown) => Promise<unknown>;
};

function getApi(): G7Api | null {
  return (window as { G7Core?: { api?: G7Api } }).G7Core?.api ?? null;
}

interface Props {
  intervalMs?: number;
}

/** 로그인 회원/관리자 접속 유지 heartbeat (사이트별 캐시 분리) */
export default function OnelivePresenceHeartbeat({ intervalMs = 20000 }: Props) {
  const { isAuthed, authReady } = useOneliveSession();
  const mountedRef = useRef(true);

  const ping = useCallback(async () => {
    const user = getCurrentUser();
    if (!user?.uuid) {
      return;
    }

    const api = getApi();
    if (!api) {
      return;
    }

    try {
      await api.post('/modules/onelive-admin/presence/ping', {
        is_admin: window.location.pathname.startsWith('/admin'),
      });
    } catch {
      // ignore heartbeat errors
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!authReady) {
      return undefined;
    }

    const hasUser = isAuthed || Boolean(getCurrentUser()?.uuid);
    if (!hasUser) {
      return undefined;
    }

    void ping();
    const timer = window.setInterval(() => {
      if (mountedRef.current) {
        void ping();
      }
    }, intervalMs);

    const core = (window as { G7Core?: { state?: { subscribe?: (cb: (state: Record<string, unknown>) => void) => () => void } } }).G7Core;
    const unsubscribe = core?.state?.subscribe?.((state) => {
      const global = (state?._global ?? state) as Record<string, unknown>;
      const nextUser = global?.currentUser as { uuid?: string } | null | undefined;
      if (nextUser?.uuid && mountedRef.current) {
        void ping();
      }
    });

    return () => {
      window.clearInterval(timer);
      unsubscribe?.();
    };
  }, [authReady, isAuthed, intervalMs, ping]);

  return null;
}
